import { Container, Sprite, type Texture } from 'pixi.js';
import { CROPS, type CropId, growingStages } from '@/data/crops';
import { VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from '@/core/viewport';
import type { CropSpriteKey } from '@/sprites/crops';
import { TILE_SIZE, type TileSpriteKey } from '@/sprites/tiles';
import type { Farmland } from '@/world/farmland';

/**
 * Tilled soil and crops, drawn over the base tilemap.
 *
 * A separate pass rather than mutating tilemap tiles: the map is static content
 * and farmland is playthrough state, and keeping them apart means the map can be
 * redrawn without touching saves. Culled and pooled like the tilemap, so an
 * entirely planted farm costs the same as an empty one off-screen.
 */

/** Stage art shared by every crop until it ripens. */
const STAGE_SPRITES: readonly CropSpriteKey[] = ['seedling', 'sprout', 'bush'];

/**
 * Which sprite a plant shows at a given stage.
 *
 * Pure and exported so the mapping is unit-testable: driving a character across
 * a farm and through a doorway to eyeball four growth stages is a slow and
 * flaky way to check an array index.
 *
 * Crops with more growing stages than there is shared art hold on the last
 * shared frame, so adding a slow crop never renders a blank tile.
 */
export function cropSpriteFor(cropId: CropId, stage: number): CropSpriteKey {
  const definition = CROPS[cropId];
  if (stage >= growingStages(definition)) return definition.ripeSprite;
  const index = Math.min(Math.max(0, stage), STAGE_SPRITES.length - 1);
  return STAGE_SPRITES[index]!;
}

export class FarmlandRenderer {
  readonly container = new Container();

  private readonly pool: Sprite[] = [];

  constructor(
    private readonly farmland: Farmland,
    private readonly tileTextures: Readonly<Record<TileSpriteKey, Texture>>,
    private readonly cropTextures: Readonly<Record<CropSpriteKey, Texture>>,
  ) {}

  render(cameraX: number, cameraY: number, dayIndex: number): void {
    const minX = Math.floor(cameraX / TILE_SIZE) - 1;
    const minY = Math.floor(cameraY / TILE_SIZE) - 1;
    const maxX = Math.ceil((cameraX + VIRTUAL_WIDTH) / TILE_SIZE);
    const maxY = Math.ceil((cameraY + VIRTUAL_HEIGHT) / TILE_SIZE);

    let used = 0;

    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        const plot = this.farmland.at(x, y);
        if (!plot?.tilled) continue;

        const soil = this.spriteAt(used++);
        soil.texture =
          plot.wateredOnDay === dayIndex
            ? this.tileTextures.soilWet
            : this.tileTextures.soil;
        soil.position.set(x * TILE_SIZE, y * TILE_SIZE);
        soil.visible = true;

        if (!plot.crop) continue;

        const crop = this.spriteAt(used++);
        crop.texture = this.cropTextures[cropSpriteFor(plot.crop.id, plot.crop.stage)];
        crop.position.set(x * TILE_SIZE, y * TILE_SIZE);
        crop.visible = true;
      }
    }

    for (let i = used; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  destroy(): void {
    this.container.destroy({ children: true });
    this.pool.length = 0;
  }

  private spriteAt(index: number): Sprite {
    let sprite = this.pool[index];
    if (!sprite) {
      sprite = new Sprite();
      this.pool.push(sprite);
      this.container.addChild(sprite);
    }
    return sprite;
  }
}
